//
// File: pluginLabelConfig.ts
// Description: Locales a plugin label may be supplied in
// Copyright: Broadlume 2025
//

/**
 * Locales a plugin label may be supplied in.
 *
 * Deliberately a superset of DEFAULT_I18N_LOCALES (the locales Homebase's own UI
 * copy is translated into, currently en/pt-BR/es). Plugin labels are data an
 * admin can supply at any time, so we accept and store every language the
 * business intends to support, ahead of the UI copy for those languages being
 * translated. A label in a locale the UI does not yet offer is simply never
 * selected -- resolution falls back to English -- and starts being used the
 * moment that locale is added to the UI.
 *
 * Must stay in sync with PLUGIN_LABEL_LOCALES in the backend plugins constants.
 * Adding a locale to both is all that is needed: the admin editor and the
 * Language Browser are driven off this list.
 */
export const PLUGIN_LABEL_LOCALES = [
  'en',
  'fr',
  'de',
  'es',
  'pt-BR',
] as const;

export type PluginLabelLocale = (typeof PLUGIN_LABEL_LOCALES)[number];

/**
 * The locale a plugin label falls back to when the viewer's locale has no label.
 */
export const PLUGIN_LABEL_FALLBACK_LOCALE: PluginLabelLocale = 'en';

/**
 * Human-readable names for the label locales, used for the admin editor field
 * labels and the Language Browser column headers.
 */
export const PLUGIN_LABEL_LOCALE_NAMES: Record<PluginLabelLocale, string> = {
  en: 'English',
  fr: 'French',
  de: 'German',
  es: 'Spanish',
  'pt-BR': 'Portuguese (Brazil)',
};
